import React, { useEffect, useState } from "react";
import Header from "../../../component/organisms/header";
import MainLayout from "../../../component/atoms/layout/mainLayout";
import Sidebar from "../../../component/organisms/sidebar";
import DetailPageLayout from "../../../component/atoms/layout/detail/detailPageLayout";
import TableTitle from "./../../../component/atoms/text/table/tableTitle";
import styled from "styled-components";
import TableText from "./../../../component/atoms/text/table/tableText";
import { formatNumberWithCommas } from "./../../../util/formatNumber";
import SelectBox from "../../../component/organisms/selectBox";
import PretendardText from "../../../component/atoms/text/pretendardText";
import AnalyticsApi from "../../../api/analytics.api";
import { errorHandler } from "../../../util/errorHandler";
import moment from "moment";
import getToday from "../../../util/getToday";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import Loading from "../../../component/templates/Loading";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { route } from "../../../router/route";

const StatisticsVisitor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ========== 필터링 ========== //
  const [selectedSort, setSelectedSort] = useState("방문자");

  useEffect(() => {
    const sort = searchParams.get("sort");

    if (sort) {
      setSelectedSort(sort === "activeUsers" ? "방문자" : "페이지뷰");
    }
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const [sortListBoxOpen, setSortListBoxOpen] = useState(false);

  const sortList = [
    {
      title: "방문자",
      value: "방문자",
    },
    {
      title: "페이지뷰",
      value: "페이지뷰",
    },
  ];

  // ========== 월간 및 연간 & 일간 평균 ========== //
  const [monthAndYearData, setMonthAndYearData] = useState({});

  const getMonthAndYearData = async () => {
    try {
      const twoYearsAgoFirst = moment()
        .subtract(2, "years")
        .startOf("year")
        .format("YYYY-MM-DD");

      const thisYearLast = moment().endOf("year").format("YYYY-MM-DD");

      const data = {
        startDate: twoYearsAgoFirst,
        endDate: thisYearLast,
        dimensions: "yearMonth",
        metrics: searchParams.get("sort") || "activeUsers",
      };

      const response = (await AnalyticsApi.getListVisitPageview(data)).data
        .content;

      return response || [];
    } catch (error) {
      errorHandler(error);
      toast(
        <>
          데이터를 불러오는 중 오류가 발생했습니다.
          <br />
          구글 애널리틱스에서 확인해주세요.
        </>
      );
    }
  };

  const {
    data: monthAndYearOriginData = [],
    isLoading: isMonthAndYearOriginLoading,
    isError: isMonthAndYearOriginError,
  } = useQuery({
    queryKey: ["monthAndYearOriginData", searchParams.get("sort")],
    queryFn: getMonthAndYearData,
    staleTime: 1000 * 60 * 15, // 15분 캐시 유지
  });

  useEffect(() => {
    if (monthAndYearOriginData.length > 0) {
      const yearData = {};

      monthAndYearOriginData?.forEach((item) => {
        const year = item?.date.split("-")[0];

        if (!yearData[year]) {
          yearData[year] = [];
        }

        yearData[year].push(item);
      });

      Object.keys(yearData)?.forEach((year) => {
        const data = yearData[year];
        const totalSum = data.reduce(
          (sum, item) => sum + Number(item.total),
          0
        );
        const averageSum = data.reduce(
          (sum, item) => sum + Number(item?.average),
          0
        );

        const totalAvg = totalSum / data.length;
        const averageAvg = averageSum / data.length;

        yearData[year] = {
          ...data,
          totalAvg: Number(totalAvg.toFixed(1)) || 0,
          averageAvg: Number(averageAvg.toFixed(1)) || 0,
        };
      });

      setMonthAndYearData(yearData);
    }
  }, [monthAndYearOriginData]);

  // ========== 최근 주별 현황 ========== //
  const [weeklyTableData, setWeeklyTableData] = useState([]);

  const getWeeklyTableData = async () => {
    try {
      const today = getToday();

      const threeWeeksAgoMonday = moment()
        .subtract(4, "weeks")
        .startOf("isoWeek")
        .format("YYYY-MM-DD");

      const data = {
        startDate: threeWeeksAgoMonday,
        endDate: today,
        dimensions: "date",
        metrics: searchParams.get("sort") || "activeUsers",
      };

      const response = (await AnalyticsApi.getListVisitPageview(data)).data
        .content;

      return response || [];
    } catch (error) {
      errorHandler(error);
      toast(
        <>
          데이터를 불러오는 중 오류가 발생했습니다.
          <br />
          구글 애널리틱스에서 확인해주세요.
        </>
      );
    }
  };

  const {
    data: weeklyOriginData = [],
    isLoading: isWeeklyOriginLoading,
    isError: isWeeklyOriginError,
  } = useQuery({
    queryKey: ["weeklyOriginData", searchParams.get("sort")],
    queryFn: getWeeklyTableData,
    staleTime: 1000 * 60 * 15, // 15분 캐시 유지
  });

  useEffect(() => {
    if (weeklyOriginData.length > 0) {
      const weeks = {
        0: [],
        1: [],
        2: [],
        3: [],
        4: [],
      };

      weeklyOriginData?.forEach((item, index) => {
        const weekNumber = Math.floor(index / 7);
        if (weeks[weekNumber]) {
          weeks[weekNumber].push(item);
        }
      });

      let previousSum = 0;

      Object.keys(weeks)?.forEach((key) => {
        const weekData = weeks[key]; // 해당 주차 데이터 배열
        const sum = weekData.reduce((acc, item) => acc + item.total, 0); // 합계 계산
        const avg = weekData.length > 0 ? sum / weekData.length : 0; // 평균 계산

        const incDec =
          previousSum === 0 ? 0 : ((sum - previousSum) / previousSum) * 100;

        previousSum = sum;

        weeks[key] = {
          data: weekData, // 원래 데이터
          sum, // 합계
          avg: Number(avg.toFixed(1)), // 평균
          incDec: Number(incDec.toFixed(2)), // 증감
        };
      });

      // 첫 번째 키 삭제
      delete weeks[0];

      setWeeklyTableData(weeks);
    }
  }, [weeklyOriginData]);

  return (
    <>
      <Header />
      <MainLayout
        style={{
          position: "relative",
          gap: 306,
        }}
      >
        <Sidebar />

        {/* 본문 영역 */}
        <DetailPageLayout>
          <TableTitle
            style={{
              marginBottom: 16,
            }}
          >
            방문자
          </TableTitle>
          <ul
            style={{
              listStyle: "disc",
              listStylePosition: "inside",
              listColor: "#aaa",
              marginBottom: 20,
            }}
          >
            <ListText>
              방문자: 내 사이트에 방문한 기기 또는 브라우저의 수입니다. 같은
              기기에서 여러번 방문해도 하루 한 번만 카운트 됩니다.
            </ListText>
            <ListText>
              페이지뷰: 기기, 브라우저 종류에 상관 없이 페이지가 호출된
              수입니다. 같은 페이지를 재방문 또는 새로고침하여도 카운트가
              됩니다.
            </ListText>
          </ul>
          <SelectBox
            isMiddle={true}
            selectedValue={selectedSort}
            isOpen={sortListBoxOpen}
            wrapperHandler={() => {
              setSortListBoxOpen(!sortListBoxOpen);
            }}
            itemList={sortList}
            selectedValueSetterFunc={(value) => {
              navigate(
                `${route.statistics_visitor}?sort=${
                  value === "방문자" ? "activeUsers" : "screenPageViews"
                }`
              );
              setSortListBoxOpen(false);
            }}
          />

          <ContentContainer>
            {/* 월간 및 연간 */}
            <ContentWrapper>
              <TableTitleText>월간 및 연간</TableTitleText>
              {isMonthAndYearOriginLoading || isMonthAndYearOriginError ? (
                <div
                  style={{
                    width: "100%",
                    height: 219,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Loading color="#3a96df" style={{ position: "static" }} />
                </div>
              ) : (
                <>
                  <table
                    style={{
                      width: "100%",
                      borderSpacing: 0,
                      borderCollapse: "collapse",
                    }}
                  >
                    <colgroup>
                      {/* 합계: 1044px */}
                      {/* 연도 */}
                      <col width="60px" />
                      {/* 1월 */}
                      <col width="91px" />
                      {/* 2월 */}
                      <col width="67px" />
                      {/* 3월 */}
                      <col width="67px" />
                      {/* 4월 */}
                      <col width="68px" />
                      {/* 5월 */}
                      <col width="67px" />
                      {/* 6월 */}
                      <col width="67px" />
                      {/* 7월 */}
                      <col width="67px" />
                      {/* 8월 */}
                      <col width="67px" />
                      {/* 9월 */}
                      <col width="67px" />
                      {/* 10월 */}
                      <col width="74px" />
                      {/* 11월 */}
                      <col width="71px" />
                      {/* 12월 */}
                      <col width="71px" />
                      {/* 평균 */}
                      <col width="140px" />
                    </colgroup>
                    <thead>
                      <tr
                        style={{
                          height: 41,
                          backgroundColor: "#313233",
                          borderTop: "0.5px solid rgba(255, 255, 255, 0.12)",
                        }}
                      >
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              paddingLeft: 24,
                            }}
                          ></TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            1월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            2월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            3월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            4월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            5월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            6월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            7월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            8월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            9월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            10월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            11월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            12월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                              paddingRight: 49,
                            }}
                          >
                            평균
                          </TableText>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(monthAndYearData)?.map((year, index) => {
                        return (
                          <>
                            <tr
                              key={year + index}
                              style={{
                                height: 59,
                                borderBottom:
                                  "0.5px solid rgba(255, 255, 255, 0.12)",
                              }}
                            >
                              <td>
                                <TableText
                                  style={{
                                    maxWidth: 195,
                                    paddingLeft: 24,
                                  }}
                                >
                                  {year}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    monthAndYearData[year][0].total
                                  )}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    monthAndYearData[year][1].total
                                  )}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    monthAndYearData[year][2].total
                                  )}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    monthAndYearData[year][3].total
                                  )}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    monthAndYearData[year][4].total
                                  )}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    monthAndYearData[year][5].total
                                  )}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    monthAndYearData[year][6].total
                                  )}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    monthAndYearData[year][7].total
                                  )}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    monthAndYearData[year][8].total
                                  )}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    monthAndYearData[year][9].total
                                  )}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    monthAndYearData[year][10].total
                                  )}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {formatNumberWithCommas(
                                    monthAndYearData[year][11].total
                                  )}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                    marginRight: 49,
                                  }}
                                >
                                  {monthAndYearData[year].totalAvg}
                                </TableText>
                              </td>
                            </tr>
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </ContentWrapper>

            {/* 일간 평균 */}
            <ContentWrapper>
              <TableTitleText>일간 평균</TableTitleText>
              {isMonthAndYearOriginLoading || isMonthAndYearOriginError ? (
                <div
                  style={{
                    width: "100%",
                    height: 219,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Loading color="#3a96df" style={{ position: "static" }} />
                </div>
              ) : (
                <>
                  <table
                    style={{
                      width: "100%",
                      borderSpacing: 0,
                      borderCollapse: "collapse",
                    }}
                  >
                    <colgroup>
                      {/* 합계: 1044px */}
                      {/* 연도 */}
                      <col width="60px" />
                      {/* 1월 */}
                      <col width="91px" />
                      {/* 2월 */}
                      <col width="67px" />
                      {/* 3월 */}
                      <col width="67px" />
                      {/* 4월 */}
                      <col width="68px" />
                      {/* 5월 */}
                      <col width="67px" />
                      {/* 6월 */}
                      <col width="67px" />
                      {/* 7월 */}
                      <col width="67px" />
                      {/* 8월 */}
                      <col width="67px" />
                      {/* 9월 */}
                      <col width="67px" />
                      {/* 10월 */}
                      <col width="74px" />
                      {/* 11월 */}
                      <col width="71px" />
                      {/* 12월 */}
                      <col width="71px" />
                      {/* 평균 */}
                      <col width="140px" />
                    </colgroup>
                    <thead>
                      <tr
                        style={{
                          height: 41,
                          backgroundColor: "#313233",
                          borderTop: "0.5px solid rgba(255, 255, 255, 0.12)",
                        }}
                      >
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              paddingLeft: 24,
                            }}
                          ></TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            1월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            2월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            3월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            4월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            5월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            6월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            7월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            8월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            9월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            10월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            11월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                            }}
                          >
                            12월
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "right",
                              paddingRight: 49,
                            }}
                          >
                            평균
                          </TableText>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(monthAndYearData)?.map((year, index) => {
                        return (
                          <>
                            <tr
                              key={year + index}
                              style={{
                                height: 59,
                                borderBottom:
                                  "0.5px solid rgba(255, 255, 255, 0.12)",
                              }}
                            >
                              <td>
                                <TableText
                                  style={{
                                    maxWidth: 195,
                                    paddingLeft: 24,
                                  }}
                                >
                                  {year}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {monthAndYearData[year][0].average}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {monthAndYearData[year][1].average}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {monthAndYearData[year][2].average}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {monthAndYearData[year][3].average}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {monthAndYearData[year][4].average}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {monthAndYearData[year][5].average}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {monthAndYearData[year][6].average}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {monthAndYearData[year][7].average}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {monthAndYearData[year][8].average}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {monthAndYearData[year][9].average}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {monthAndYearData[year][10].average}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                  }}
                                >
                                  {monthAndYearData[year][11].average}
                                </TableText>
                              </td>
                              <td>
                                <TableText
                                  style={{
                                    textAlign: "right",
                                    marginRight: 49,
                                  }}
                                >
                                  {monthAndYearData[year].averageAvg}
                                </TableText>
                              </td>
                            </tr>
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </ContentWrapper>

            {/* 최근 주별 현황 */}
            <ContentWrapper>
              <TableTitleText>최근 주별 현황</TableTitleText>
              {isWeeklyOriginLoading || isWeeklyOriginError ? (
                <div
                  style={{
                    width: "100%",
                    height: 497.5,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Loading color="#3a96df" style={{ position: "static" }} />
                </div>
              ) : (
                <>
                  <table
                    style={{
                      width: "100%",
                      borderSpacing: 0,
                      borderCollapse: "collapse",
                    }}
                  >
                    <colgroup>
                      {/* 합계: 1044px */}
                      {/* 월 */}
                      <col width="104px" />
                      {/* 화 */}
                      <col width="104px" />
                      {/* 수 */}
                      <col width="104px" />
                      {/* 목 */}
                      <col width="104px" />
                      {/* 금 */}
                      <col width="104px" />
                      {/* 토 */}
                      <col width="104px" />
                      {/* 일 */}
                      <col width="104px" />
                      {/* 합계 */}
                      <col width="104px" />
                      {/* 평균 */}
                      <col width="104px" />
                      {/* 증감 */}
                      <col width="108px" />
                    </colgroup>
                    <thead>
                      <tr
                        style={{
                          height: 41,
                          backgroundColor: "#313233",
                          borderTop: "0.5px solid rgba(255, 255, 255, 0.12)",
                        }}
                      >
                        <th
                          style={{
                            borderRight: "1px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "center",
                            }}
                          >
                            월
                          </TableText>
                        </th>
                        <th
                          style={{
                            borderRight: "1px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "center",
                            }}
                          >
                            화
                          </TableText>
                        </th>
                        <th
                          style={{
                            borderRight: "1px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "center",
                            }}
                          >
                            수
                          </TableText>
                        </th>
                        <th
                          style={{
                            borderRight: "1px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "center",
                            }}
                          >
                            목
                          </TableText>
                        </th>
                        <th
                          style={{
                            borderRight: "1px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "center",
                            }}
                          >
                            금
                          </TableText>
                        </th>
                        <th
                          style={{
                            borderRight: "1px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "center",
                            }}
                          >
                            토
                          </TableText>
                        </th>
                        <th
                          style={{
                            borderRight: "1px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "center",
                            }}
                          >
                            일
                          </TableText>
                        </th>
                        <th
                          style={{
                            borderRight: "1px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "center",
                            }}
                          >
                            합계
                          </TableText>
                        </th>
                        <th
                          style={{
                            borderRight: "1px solid rgba(255, 255, 255, 0.12)",
                          }}
                        >
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "center",
                            }}
                          >
                            평균
                          </TableText>
                        </th>
                        <th>
                          <TableText
                            style={{
                              fontWeight: "600",
                              lineHeight: "22.75px",
                              textAlign: "center",
                            }}
                          >
                            증감
                          </TableText>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(weeklyTableData)?.map((week, index) => {
                        return (
                          <>
                            <tr
                              key={index}
                              style={{
                                height: 113,
                                borderBottom:
                                  "0.5px solid rgba(255, 255, 255, 0.12)",
                              }}
                            >
                              <td
                                style={{
                                  borderRight:
                                    "1px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: 113,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    padding: "8px 12px",
                                  }}
                                >
                                  <TableText
                                    style={{
                                      alignSelf: "flex-start",
                                      color: "rgba(181, 181, 181, 0.5)",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[0]?.date
                                      ? moment(
                                          weeklyTableData[week].data[0].date
                                        ).format("MM/DD")
                                      : ""}
                                  </TableText>
                                  <TableText
                                    style={{
                                      alignSelf: "flex-end",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[0]?.total
                                      ? formatNumberWithCommas(
                                          weeklyTableData[week].data[0].total
                                        )
                                      : "0"}
                                  </TableText>
                                </div>
                              </td>
                              <td
                                style={{
                                  borderRight:
                                    "1px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: 113,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    padding: "8px 12px",
                                  }}
                                >
                                  <TableText
                                    style={{
                                      alignSelf: "flex-start",
                                      color: "rgba(181, 181, 181, 0.5)",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[1]?.date
                                      ? moment(
                                          weeklyTableData[week].data[1].date
                                        ).format("MM/DD")
                                      : ""}
                                  </TableText>
                                  <TableText
                                    style={{
                                      alignSelf: "flex-end",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[1]?.total
                                      ? formatNumberWithCommas(
                                          weeklyTableData[week].data[1].total
                                        )
                                      : "0"}
                                  </TableText>
                                </div>
                              </td>
                              <td
                                style={{
                                  borderRight:
                                    "1px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: 113,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    padding: "8px 12px",
                                  }}
                                >
                                  <TableText
                                    style={{
                                      alignSelf: "flex-start",
                                      color: "rgba(181, 181, 181, 0.5)",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[2]?.date
                                      ? moment(
                                          weeklyTableData[week].data[2].date
                                        ).format("MM/DD")
                                      : ""}
                                  </TableText>
                                  <TableText
                                    style={{
                                      alignSelf: "flex-end",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[2]?.total
                                      ? formatNumberWithCommas(
                                          weeklyTableData[week].data[2].total
                                        )
                                      : "0"}
                                  </TableText>
                                </div>
                              </td>
                              <td
                                style={{
                                  borderRight:
                                    "1px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: 113,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    padding: "8px 12px",
                                  }}
                                >
                                  <TableText
                                    style={{
                                      alignSelf: "flex-start",
                                      color: "rgba(181, 181, 181, 0.5)",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[3]?.date
                                      ? moment(
                                          weeklyTableData[week].data[3].date
                                        ).format("MM/DD")
                                      : ""}
                                  </TableText>
                                  <TableText
                                    style={{
                                      alignSelf: "flex-end",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[3]?.total
                                      ? formatNumberWithCommas(
                                          weeklyTableData[week].data[3].total
                                        )
                                      : "0"}
                                  </TableText>
                                </div>
                              </td>
                              <td
                                style={{
                                  borderRight:
                                    "1px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: 113,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    padding: "8px 12px",
                                  }}
                                >
                                  <TableText
                                    style={{
                                      alignSelf: "flex-start",
                                      color: "rgba(181, 181, 181, 0.5)",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[4]?.date
                                      ? moment(
                                          weeklyTableData[week].data[4].date
                                        ).format("MM/DD")
                                      : ""}
                                  </TableText>
                                  <TableText
                                    style={{
                                      alignSelf: "flex-end",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[4]?.total
                                      ? formatNumberWithCommas(
                                          weeklyTableData[week].data[4].total
                                        )
                                      : "0"}
                                  </TableText>
                                </div>
                              </td>
                              <td
                                style={{
                                  borderRight:
                                    "1px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: 113,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    padding: "8px 12px",
                                  }}
                                >
                                  <TableText
                                    style={{
                                      alignSelf: "flex-start",
                                      color: "rgba(181, 181, 181, 0.5)",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[5]?.date
                                      ? moment(
                                          weeklyTableData[week].data[5].date
                                        ).format("MM/DD")
                                      : ""}
                                  </TableText>
                                  <TableText
                                    style={{
                                      alignSelf: "flex-end",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[5]?.total
                                      ? formatNumberWithCommas(
                                          weeklyTableData[week].data[5].total
                                        )
                                      : "0"}
                                  </TableText>
                                </div>
                              </td>
                              <td
                                style={{
                                  borderRight:
                                    "1px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: 113,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    padding: "8px 12px",
                                  }}
                                >
                                  <TableText
                                    style={{
                                      alignSelf: "flex-start",
                                      color: "rgba(181, 181, 181, 0.5)",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[6]?.date
                                      ? moment(
                                          weeklyTableData[week].data[6].date
                                        ).format("MM/DD")
                                      : ""}
                                  </TableText>
                                  <TableText
                                    style={{
                                      alignSelf: "flex-end",
                                    }}
                                  >
                                    {weeklyTableData[week]?.data[6]?.total
                                      ? formatNumberWithCommas(
                                          weeklyTableData[week].data[6].total
                                        )
                                      : "0"}
                                  </TableText>
                                </div>
                              </td>
                              {/* 합계 */}
                              <td
                                style={{
                                  borderRight:
                                    "1px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: 113,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "flex-end",
                                    padding: "8px 12px",
                                  }}
                                >
                                  <TableText
                                    style={{
                                      alignSelf: "flex-end",
                                    }}
                                  >
                                    {weeklyTableData[week]?.sum
                                      ? formatNumberWithCommas(
                                          weeklyTableData[week].sum
                                        )
                                      : "0"}
                                  </TableText>
                                </div>
                              </td>
                              {/* 평균 */}
                              <td
                                style={{
                                  borderRight:
                                    "1px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: 113,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "flex-end",
                                    padding: "8px 12px",
                                  }}
                                >
                                  <TableText
                                    style={{
                                      alignSelf: "flex-end",
                                    }}
                                  >
                                    {weeklyTableData[week]?.avg
                                      ? weeklyTableData[week].avg
                                      : "0"}
                                  </TableText>
                                </div>
                              </td>
                              {/* 증갑 */}
                              <td
                                style={{
                                  borderRight:
                                    "1px solid rgba(255, 255, 255, 0.12)",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: 113,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "flex-end",
                                    padding: "8px 12px",
                                  }}
                                >
                                  <TableText
                                    style={{
                                      alignSelf: "flex-end",
                                      color:
                                        weeklyTableData[week]?.incDec >= 0
                                          ? "#009bde"
                                          : "#cf6679",
                                    }}
                                  >
                                    {weeklyTableData[week]?.incDec
                                      ? `${weeklyTableData[week].incDec}%`
                                      : "0%"}
                                  </TableText>
                                </div>
                              </td>
                            </tr>
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </ContentWrapper>
          </ContentContainer>
        </DetailPageLayout>
      </MainLayout>
    </>
  );
};

export default StatisticsVisitor;

const ContentContainer = styled.div.attrs((props) => {})`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`;

const ContentWrapper = styled.div.attrs((props) => {})`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 40px;
`;

const ListText = styled.li.attrs((props) => {})`
  font-family: "Pretendard";
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  color: #aaa;
`;

const TableTitleText = styled(PretendardText).attrs((props) => {})`
  font-weight: 600;
  font-size: 18px;
  line-height: 32.5px;
  color: #f3f3f3;
`;
